import { InMemoryCache, defaultDataIdFromObject } from "@apollo/client"
import { paginatedField } from "@/v2-backoffice-client/components/table"
import { userCacheFields } from "@/v2-backoffice-client/user/user.cache"
import { tenantCacheFields } from "@/v2-backoffice-client/tenant/tenant.cache"
import { customerCacheFields } from "@/v2-backoffice-client/customer/customer.cache"
import { layerCacheFields } from "@/v2-backoffice-client/layer/layer.cache"
import { layerCategoryCacheFields } from "@/v2-backoffice-client/layer/category/layer.category.cache"
import { basemapCacheFields } from "@/v2-backoffice-client/basemap/basemap.cache"
import { navLayerCacheFields } from "@/v2-backoffice-client/navlayer/navlayer.cache"
import { invitationCacheFields } from "@/v2-backoffice-client/invitation/invitation.cache"
import { signInCacheFields } from "@/v2-backoffice-client/signIn/signin.cache"
import { userLoginCacheFields } from "@/v2-backoffice-client/userLogin/userLogin.cache"
import {
  uptimeCacheFields,
  uptimeTypePolicies,
} from "@/v2-backoffice-client/uptime/uptime.cache"
import { translationTypePolicies } from "@/v2-backoffice-client/translation/translation.cache"
import { tenantPrivacyTypePolicies } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.cache"

const NO_MERGE = { merge: false }

export const cache = new InMemoryCache({
  dataIdFromObject(obj) {
    const id = obj.Id || obj.Code || obj.FeatureCode
    return id ? `${obj.__typename}:${id}` : defaultDataIdFromObject(obj)
  },
  typePolicies: {
    // Keyed by the app-owned Id: ExternalCustomerId (the legacy HubSpot
    // link) is nullable now that customers can be app-created before being
    // linked — keying on it would collapse every unlinked customer into
    // one cache entry.
    Customer: {
      keyFields: ["Id"],
    },
    // Authoring rows are keyed per environment; the FeatureCode-only
    // dataIdFromObject fallback would merge a layer's dev and prod rows.
    StudioLayer: {
      keyFields: ["FeatureCode", "Environment"],
    },
    // A temporal version is identified by WHEN it became valid, not just
    // by layer: the FeatureCode fallback in dataIdFromObject collapsed
    // every version of a layer into ONE cache record, so the history list
    // rendered N copies of the latest version.
    StudioLayerVersion: {
      keyFields: ["FeatureCode", "Environment", "ValidFrom"],
    },
    // Same trap for a license's versions: Id is the license id on every
    // one of them, so the fallback merged the whole history into one record
    // and the dialog showed the current version N times (Atlas Eiendom,
    // 2 Nov 2026 — nobody could see the trial → takeover → Expired →
    // Scheduled story it held).
    LicenseHistoryEntry: {
      keyFields: ["Id", "VersionStart"],
    },
    // Rows of the refusals feed carry a FeatureCode, which the fallback
    // would take as their identity and fold every row of one feature into
    // one record. They have no identity of their own (one person × day ×
    // feature × reason): keep them embedded in the list.
    UsageLicenseDeniedEvent: {
      keyFields: false,
    },
    // A license's coverage is identified by its license; its issues carry a
    // Code shared by every license with the same problem, and its segments
    // nothing at all — both stay embedded in their license.
    LicenseCoverage: {
      keyFields: ["LicenseId"],
    },
    LicenseCoverageIssue: {
      keyFields: false,
    },
    LicenseCoverageSegment: {
      keyFields: false,
    },
    ...uptimeTypePolicies,
    ...translationTypePolicies,
    ...tenantPrivacyTypePolicies,
    Query: {
      fields: {
        getUsers: paginatedField(),
        ...userCacheFields,
        getUserStatusCounts: NO_MERGE,
        getUserTenants: NO_MERGE,

        getTenants: paginatedField(),
        ...tenantCacheFields,
        getTenantCounts: NO_MERGE,
        getTenantUsers: NO_MERGE,

        getAgentConversations: paginatedField(),
        getAgentConversationTurns: NO_MERGE,

        getCustomers: paginatedField(),
        ...customerCacheFields,
        getCustomerTenants: NO_MERGE,
        getCustomerUsers: NO_MERGE,

        getInvitations: paginatedField(),
        ...invitationCacheFields,

        ...signInCacheFields,
        ...userLoginCacheFields,

        getLayers: NO_MERGE,
        ...layerCacheFields,

        getLayersByCategoryCode: NO_MERGE,
        getLayerCategories: NO_MERGE,
        ...layerCategoryCacheFields,

        getLayerTypes: NO_MERGE,

        getBasemaps: NO_MERGE,
        ...basemapCacheFields,

        getNavLayers: NO_MERGE,
        ...navLayerCacheFields,

        // FeatureCountry rows carry no identity of their own, so the whole
        // list is the cached value and a save must replace it wholesale.
        getFeatureCountries: NO_MERGE,

        ...uptimeCacheFields,
      },
    },
  },
})
